// SiteUp: notas de versao (tela Novidades). Fonte unica, versionada no repo.
//
// POR QUE AQUI E NAO NO BANCO: a nota descreve exatamente o codigo da imagem
// que a pessoa esta rodando. Viajando junto com a imagem, staging e producao
// nunca contam historia diferente do que esta no ar, e ninguem precisa
// escrever no banco a cada deploy.
//
// POR QUE EM helper/ E NAO NA PASTA DA TELA: nenhum sourceGlob do
// onboardingRegistry cobre `helper/`, entao publicar nota nova nao dispara o
// gate onboarding-impact do CI. Mexer na TELA (routes/dashboard/releaseNotes)
// dispara, e ai o bump da dica `release_notes` e devido.
//
// QUEM ESCREVE: a sessao que fecha a versao, no mesmo PR que corta a imagem.
// Uma entrada por imagem publicada, do build mais novo para o mais antigo.
//
// REGRA DE VOZ (o spec releaseNotes.spec.js reprova quem furar):
// - so o que o cliente ve na tela. Refatoracao, spec e policy nao entram.
// - correcao NAO e categoria: ela entra como `improvement`, escrita pelo lado
//   de quem usa. Errado: "corrigido o erro 500 na tela de Integracoes".
//   Certo: "a tela de Integracoes abre de novo".
// - proibido escrever bug, fix, erro, falha, crash, corrigido, regressao,
//   hotfix, patch, deploy, endpoint, backend.
// - sem travessao. A marca e SiteUP.
// - o rotulo `version` e escrito a mao e precisa casar com a tag da imagem.
//
// SHAPE: { build, version, date, prs, items: [{ id, kind, world, titleKey,
// bodyKey, routeName?, view?, coachKey? }] }. As chaves i18n sao escopadas
// pelo build (RELEASE_NOTES.R895.<ID>.TITLE), entao o `id` so precisa ser
// unico dentro da versao.
//
// COBERTURA: o 4o argumento leva { prs: [numeros] } com os pull requests que a
// imagem carrega. Nao vai pra tela; e o que o gate do build usa pra provar que
// a nota conta a imagem inteira. PR que nao muda nada visivel entra em `prs`
// mesmo assim, sem item: o campo diz "eu olhei", nao "isso virou novidade".

export const RELEASE_KINDS = Object.freeze({
  NEW: 'new',
  IMPROVEMENT: 'improvement',
});

const { NEW, IMPROVEMENT } = RELEASE_KINDS;

// `prs` lista os pull requests que a imagem carrega. Nao aparece na tela: e o
// que deixa o gate do build provar que a nota conta a imagem INTEIRA, em vez
// de so provar que existe alguma nota. O gate deriva os PRs reais do range de
// commits e cobra cada numero que nao estiver aqui. Ver
// /root/.hermes/scripts/chatwoot-release-note-gate.py na VPS.
const release = (build, date, items, meta = {}) =>
  Object.freeze({
    build,
    version: `v${build}`,
    date,
    prs: Object.freeze([...(meta.prs || [])]),
    items: Object.freeze(
      items.map(entry =>
        Object.freeze({
          ...entry,
          titleKey: `RELEASE_NOTES.R${build}.${entry.id}.TITLE`,
          bodyKey: `RELEASE_NOTES.R${build}.${entry.id}.BODY`,
        })
      )
    ),
  });

export const RELEASES = Object.freeze([
  // Botões de resposta dentro do balão no Instagram e variável de primeiro
  // nome nos Chat Flows (pedido do fluxo de story do Momentum Mentalma).
  // prettier-ignore
  release(1016, '2026-09-27', [
    {
      id: 'BOTOES_NO_BALAO_INSTAGRAM',
      kind: NEW,
      world: 'automation',
      routeName: 'conversation_workflows_index',
      coachKey: 'conversation_workflows_edit',
    },
  ], { prs: [1180] }),
  // Fila depois da segunda leva (roadmap-central-ia.md D30, D36 a D39, e
  // roadmap-ficha-lead.md). Mesma forma da 1014: uma linha só por causa da
  // asserção de build que procura `release(1015`.
  // prettier-ignore
  release(1015, '2026-09-26', [
    {
      id: 'MODO_OPORTUNIDADE',
      kind: NEW,
      world: 'ai_center',
      routeName: 'ai_center',
      coachKey: 'ai_center',
    },
    {
      id: 'RESUMO_DO_VIDEO_E_ONDE_ENCONTRAR',
      kind: NEW,
      world: 'ai_center',
      routeName: 'ai_center',
      coachKey: 'ai_center',
    },
    {
      id: 'CHAT_FLOWS_NO_FACEBOOK',
      kind: NEW,
      world: 'automation',
      routeName: 'conversation_workflows_index',
      coachKey: 'conversation_workflows_edit',
    },
    {
      id: 'FICHA_DO_LEAD',
      kind: NEW,
      world: 'attendance',
      routeName: 'inbox_conversation',
      coachKey: 'inbox_conversation',
    },
  ]),
  // Segunda leva da Central da IA (roadmap-central-ia.md, D13 a D32). Os PRs
  // entram em `prs` quando a integração abrir o pull request dela. Fica numa
  // linha só, como a 1013, porque a asserção de build procura `release(1014`
  // e o prettier quebraria a chamada quando o quarto argumento entrar.
  // prettier-ignore
  release(1014, '2026-09-25', [
    {
      id: 'KANBAN_E_CONVERSOES_PEDEM_APROVACAO',
      kind: NEW,
      world: 'ai_center',
      routeName: 'ai_center',
      coachKey: 'ai_center',
    },
    {
      id: 'RETOMADAS_LIGACOES_E_REPORTS',
      kind: NEW,
      world: 'ai_center',
      routeName: 'ai_center',
      coachKey: 'ai_center',
    },
    {
      id: 'AGENTES_COM_BASE_E_TIPOS_DA_CONTA',
      kind: IMPROVEMENT,
      world: 'ai_center',
      routeName: 'ai_center',
      coachKey: 'ai_center',
    },
    {
      id: 'LIMITE_POR_TOKEN_DA_API',
      kind: IMPROVEMENT,
      world: 'system',
    },
  ]),
  // Redesign da Central da IA (roadmap-central-ia.md, D1 a D11). Fica numa
  // linha só porque a asserção de build procura `release(1013`.
  // prettier-ignore
  release(1013, '2026-09-25', [
    {
      id: 'CENTRAL_POR_AREA',
      kind: NEW,
      world: 'ai_center',
      routeName: 'ai_center',
      coachKey: 'ai_center',
    },
    {
      id: 'COMENTARIOS_COM_CARA_DE_REDE',
      kind: NEW,
      world: 'ai_center',
      routeName: 'ai_center',
      coachKey: 'ai_center',
    },
    {
      id: 'CAMPANHA_AGRUPADA',
      kind: NEW,
      world: 'ai_center',
      routeName: 'ai_center',
      coachKey: 'ai_center',
    },
    {
      id: 'AJUSTES_DA_IA',
      kind: IMPROVEMENT,
      world: 'system',
      routeName: 'settings_menu',
      coachKey: 'settings_menu',
    },
  ], { prs: [1161, 1162] }),
  release(
    1012,
    '2026-09-21',
    [
      {
        id: 'CENTRAL_DA_IA',
        kind: NEW,
        world: 'ai_center',
        routeName: 'ai_center',
        coachKey: 'ai_center',
      },
      {
        id: 'AJUDA_DO_SISTEMA',
        kind: IMPROVEMENT,
        world: 'system',
        routeName: 'settings_menu',
        coachKey: 'settings_menu',
      },
      {
        id: 'FLUXOS_RESPEITAM_A_ESCOLHA',
        kind: IMPROVEMENT,
        world: 'automation',
        routeName: 'conversation_workflows_index',
        coachKey: 'conversation_workflows_index',
      },
      {
        id: 'AJUSTES_COM_MAIS_ESPACO',
        kind: IMPROVEMENT,
        world: 'system',
        routeName: 'settings_menu',
        coachKey: 'settings_menu',
      },
    ],
    { prs: [1019, 1025, 1071, 1073, 1074, 1075] }
  ),
  release(
    1011,
    '2026-09-20',
    [
      {
        id: 'CARTAO_ABRE_A_CONVERSA',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_board',
        coachKey: 'kanban_board',
      },
      {
        id: 'QUADRO_ABRE_MAIS_RAPIDO',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_board',
        coachKey: 'kanban_board',
      },
      {
        id: 'IA_MOSTRA_COMO_TRABALHA',
        kind: NEW,
        world: 'analytics',
        routeName: 'ai_reports',
        coachKey: 'ai_reports',
      },
      {
        id: 'CAMPANHA_PASSO_A_PASSO',
        kind: IMPROVEMENT,
        world: 'marketing',
        routeName: 'campaigns_whatsapp_index',
        coachKey: 'campaigns_whatsapp_index',
      },
      {
        id: 'CADA_CONTA_NO_SEU_LUGAR',
        kind: IMPROVEMENT,
        world: 'analytics',
        routeName: 'account_overview_reports',
        coachKey: 'account_overview_reports',
      },
    ],
    { prs: [1059, 1060, 1061, 1062, 1064, 1065, 1066] }
  ),
  release(
    1010,
    '2026-09-19',
    [
      {
        id: 'CAMPANHA_NUMERO_CONFERIVEL',
        kind: IMPROVEMENT,
        world: 'analytics',
        routeName: 'campaign_reports',
        coachKey: 'campaign_reports',
      },
      {
        id: 'PREVISAO_SO_COM_ETAPA_DE_GANHO',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_reports',
        coachKey: 'kanban_reports',
      },
      {
        id: 'SEM_DADO_NAO_E_NUMERO_BOM',
        kind: IMPROVEMENT,
        world: 'analytics',
        routeName: 'conversation_reports',
        coachKey: 'conversation_reports',
      },
    ],
    { prs: [1053, 1054] }
  ),
  release(1009, '2026-09-19', [
    {
      id: 'CAMPANHA_CARTAO_SEM_CONTATO',
      kind: IMPROVEMENT,
      world: 'marketing',
      routeName: 'campaigns_whatsapp_index',
      coachKey: 'campaigns_whatsapp_index',
    },
    {
      id: 'KANBAN_LINK_DO_FUNIL',
      kind: IMPROVEMENT,
      world: 'sales',
      routeName: 'kanban_board',
      coachKey: 'kanban_board',
    },
  ]),
  release(1008, '2026-09-19', [
    {
      id: 'CAMPANHA_EM_ORDEM',
      kind: IMPROVEMENT,
      world: 'marketing',
      routeName: 'campaigns_whatsapp_index',
      coachKey: 'campaigns_whatsapp_index',
    },
    {
      id: 'KANBAN_CRIAR_CAMPANHA',
      kind: IMPROVEMENT,
      world: 'sales',
      routeName: 'kanban_board',
      coachKey: 'kanban_board',
    },
    {
      id: 'GRUPOS_EM_ORDEM',
      kind: IMPROVEMENT,
      world: 'marketing',
      routeName: 'whatsapp_groups_campaigns',
      coachKey: 'whatsapp_groups_campaigns',
    },
    {
      id: 'VENDAS_FUNIL_DE_HOJE',
      kind: IMPROVEMENT,
      world: 'sales',
      routeName: 'kanban_reports',
      coachKey: 'kanban_reports',
    },
  ]),
  release(
    1007,
    '2026-09-19',
    [
      {
        id: 'RELATORIOS_UMA_CASA',
        kind: IMPROVEMENT,
        world: 'analytics',
        routeName: 'conversation_reports',
        coachKey: 'conversation_reports',
      },
      {
        id: 'RELATORIOS_FUSO_DA_CONTA',
        kind: IMPROVEMENT,
        world: 'analytics',
        routeName: 'general_settings_index',
        coachKey: 'general_settings_index',
      },
      {
        id: 'VENDAS_POR_FECHAMENTO',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_reports',
        coachKey: 'kanban_reports',
      },
      {
        id: 'KANBAN_ETAPA_DE_GANHO',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_board',
        coachKey: 'kanban_board',
      },
      {
        id: 'IA_USO_MEDIDO',
        kind: IMPROVEMENT,
        world: 'analytics',
        routeName: 'ai_reports',
        coachKey: 'ai_reports',
      },
      {
        id: 'CAMPANHAS_COM_LASTRO',
        kind: IMPROVEMENT,
        world: 'analytics',
        routeName: 'campaign_reports',
        coachKey: 'campaign_reports',
      },
    ],
    { prs: [1041, 1042, 1043, 1044, 1045, 1046] }
  ),
  release(
    1006,
    '2026-09-19',
    [
      {
        id: 'KANBAN_COMPARTILHAR_COM_COLEGA',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_board',
        coachKey: 'kanban_board',
      },
    ],
    { prs: [1040] }
  ),
  release(
    1005,
    '2026-09-19',
    [
      {
        id: 'AGENDAMENTO_UNICO',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_board',
        view: 'agenda',
        coachKey: 'kanban_board__agenda',
      },
      {
        id: 'KANBAN_CONFIGURAR',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_board',
        coachKey: 'kanban_board',
      },
      {
        id: 'KANBAN_SELECAO_E_DISPARO',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_board',
        coachKey: 'kanban_board',
      },
      {
        id: 'TAREFA_LINHA_RAPIDA',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'tasks',
        coachKey: 'tasks',
      },
    ],
    { prs: [1036, 1037, 1038, 1039] }
  ),
  release(
    1004,
    '2026-09-18',
    [
      {
        id: 'KANBAN_BUSCA_NO_LUGAR',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_board',
        coachKey: 'kanban_board',
      },
      {
        id: 'TAREFA_NOVA_COMPLETA',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'tasks',
        coachKey: 'tasks',
      },
    ],
    { prs: [1034] }
  ),
  release(
    1003,
    '2026-09-18',
    [
      {
        id: 'KANBAN_BARRA_E_CARD',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_board',
        coachKey: 'kanban_board',
      },
      {
        id: 'KANBAN_PROXIMO_PASSO',
        kind: NEW,
        world: 'sales',
        routeName: 'kanban_board',
        coachKey: 'kanban_board',
      },
      {
        id: 'KANBAN_PAINEL_DO_CARD',
        kind: NEW,
        world: 'sales',
        routeName: 'kanban_board',
        coachKey: 'kanban_board',
      },
      {
        id: 'TAREFAS_POR_VENCIMENTO',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'tasks',
        coachKey: 'tasks',
      },
      {
        id: 'AGENDA_COM_TAREFAS',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'kanban_board',
        view: 'agenda',
        coachKey: 'kanban_board__agenda',
      },
    ],
    { prs: [1026, 1027, 1028, 1029, 1030, 1031, 1032, 1033, 1034] }
  ),
  release(
    1002,
    '2026-09-17',
    [
      {
        id: 'HUB_CENTRAL',
        kind: IMPROVEMENT,
        world: 'system',
        routeName: 'focus_hub',
        coachKey: 'focus_hub',
      },
      {
        id: 'HUB_ATENDIMENTO',
        kind: IMPROVEMENT,
        world: 'attendance',
        coachKey: 'hub_world_home',
      },
      {
        id: 'COMUNIDADE_NO_ASSISTENTE',
        kind: NEW,
        world: 'marketing',
        routeName: 'whatsapp_groups_campaigns',
        coachKey: 'whatsapp_groups_campaigns',
      },
      {
        id: 'REACAO_WHATSAPP_OFICIAL',
        kind: IMPROVEMENT,
        world: 'attendance',
        routeName: 'inbox_conversation',
        coachKey: 'inbox_conversation',
      },
    ],
    { prs: [1005, 1020, 1021, 1022, 1023] }
  ),
  release(
    1000,
    '2026-09-12',
    [
      {
        id: 'FLOW_BUILDER_BOTOES',
        kind: NEW,
        world: 'automation',
        routeName: 'conversation_workflows_index',
        coachKey: 'conversation_workflows_edit',
      },
      {
        id: 'COMENTARIO_NO_FLOW',
        kind: NEW,
        world: 'automation',
        routeName: 'conversation_workflows_index',
        coachKey: 'conversation_workflows_index',
      },
      {
        id: 'HUB_POR_VERBOS',
        kind: IMPROVEMENT,
        world: 'system',
        routeName: 'focus_hub',
        coachKey: 'focus_hub',
      },
      {
        id: 'TEMA_PADRAO_ESCURO',
        kind: IMPROVEMENT,
        world: 'system',
      },
    ],
    { prs: [1000, 1002, 1001, 999, 991, 995, 996, 998] }
  ),
  release(
    999,
    '2026-09-11',
    [
      { id: 'COMENTARIO_VIRA_DIRECT', kind: IMPROVEMENT, world: 'marketing' },
      { id: 'LINK_COMO_BOTAO', kind: NEW, world: 'marketing' },
    ],
    { prs: [991, 995, 996, 998] }
  ),
  release(
    998,
    '2026-09-10',
    [
      { id: 'RESPONSAVEIS_POR_CAIXA', kind: NEW, world: 'attendance' },
      { id: 'HISTORICO_ENCERRAMENTO', kind: IMPROVEMENT, world: 'sales' },
    ],
    {
      prs: [
        845, 895, 896, 897, 898, 899, 900, 901, 902, 903, 904, 905, 906, 907,
        908, 909, 910, 911, 912, 913, 914, 915, 916, 917, 919, 920, 921, 922,
        923, 924, 925, 926, 927, 928, 930, 931, 934, 961, 962, 963, 966, 967,
        968, 969, 972, 974, 975, 976, 977, 978, 979, 980, 981, 982, 984, 988,
        990, 992, 993, 994, 995, 996,
      ],
    }
  ),
  release(
    997,
    '2026-09-10',
    [
      { id: 'MENSAGENS_SUAVES', kind: IMPROVEMENT, world: 'attendance' },
      { id: 'BRASA_MENU', kind: IMPROVEMENT, world: 'attendance' },
      { id: 'KANBAN_COMPACTO', kind: IMPROVEMENT, world: 'attendance' },
      { id: 'IA_MULTIMODAL', kind: IMPROVEMENT, world: 'attendance' },
    ],
    {
      prs: [
        845, 895, 896, 897, 898, 899, 900, 901, 902, 903, 904, 905, 906, 907,
        908, 909, 910, 911, 912, 913, 914, 915, 916, 917, 919, 920, 921, 922,
        923, 924, 925, 926, 927, 928, 930, 931, 934, 961, 962, 963, 966, 967,
        968, 969, 972, 974, 975, 976, 977, 978, 979, 980, 981, 982, 984, 988,
        990, 992, 993, 994,
      ],
    }
  ),
  release(
    996,
    '2026-09-10',
    [
      { id: 'TEMPLATES_PASTAS', kind: NEW, world: 'attendance' },
      { id: 'FORMULARIOS_LEGIVEIS', kind: IMPROVEMENT, world: 'attendance' },
      { id: 'ENVIO_CONFIRMADO', kind: IMPROVEMENT, world: 'attendance' },
      { id: 'CONVERSAS_ESPACO', kind: IMPROVEMENT, world: 'attendance' },
    ],
    {
      prs: [
        845, 895, 896, 897, 898, 899, 900, 901, 902, 903, 904, 905, 906, 907,
        908, 909, 910, 911, 912, 913, 914, 915, 916, 917, 919, 920, 921, 922,
        923, 924, 925, 926, 927, 928, 930, 931, 934, 961, 962, 963, 966, 967,
        968, 969, 972, 974, 975, 976, 977, 978, 979, 980, 981, 982, 984, 988,
      ],
    }
  ),
  release(
    995,
    '2026-09-04',
    [
      {
        id: 'SUGESTAO_SEMPRE_EM_PORTUGUES',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [919] }
  ),
  release(
    994,
    '2026-09-04',
    [
      {
        id: 'FILTRO_POR_DATA_VOLTA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [917] }
  ),
  release(
    993,
    '2026-09-04',
    [
      {
        id: 'SUGESTAO_CONSULTA_CERTA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'RASCUNHO_NAO_NASCE_EM_REACAO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'USO_DA_SUGESTAO',
        kind: NEW,
        world: 'attendance',
      },
      {
        id: 'SUGESTAO_LIMITE_TENTATIVAS',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [911, 912, 913, 914] }
  ),
  release(
    992,
    '2026-09-04',
    [
      {
        id: 'SUGESTAO_SITUACAO_ALUNO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'RASCUNHO_VAZIO_NAO_CHEGA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'SUGESTAO_SO_O_QUE_APARECEU',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'RASCUNHO_CONFERE_PEDIDO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [907, 908] }
  ),
  release(
    991,
    '2026-09-04',
    [
      {
        id: 'SUGESTAO_ULTIMA_FALA_CLIENTE',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      { id: 'RASCUNHO_DATA_HORA', kind: IMPROVEMENT, world: 'attendance' },
      { id: 'RASCUNHO_CONTEXTO', kind: IMPROVEMENT, world: 'attendance' },
      { id: 'RASCUNHO_SEM_INVENTAR', kind: IMPROVEMENT, world: 'attendance' },
      { id: 'DIGITANDO_NO_WHATSAPP', kind: NEW, world: 'attendance' },
      {
        id: 'CAMPANHA_REAPROVEITA_CONVERSA',
        kind: IMPROVEMENT,
        world: 'marketing',
      },
      { id: 'RESPONSAVEL_NA_LISTA', kind: IMPROVEMENT, world: 'attendance' },
      { id: 'MODO_FOCO_MENU', kind: NEW, world: 'system' },
      { id: 'CONTATO_JA_EXISTE', kind: IMPROVEMENT, world: 'sales' },
    ],
    {
      prs: [
        845, 878, 879, 880, 881, 883, 884, 885, 886, 887, 889, 890, 891, 892,
        893, 894, 895, 896, 897, 898, 899, 900, 901, 902, 903, 904,
      ],
    }
  ),
  release(
    982,
    '2026-09-02',
    [{ id: 'ABA_ACOES_ABRE_SEM_ESPERA', kind: IMPROVEMENT, world: 'system' }],
    { prs: [866, 867] }
  ),
  release(
    981,
    '2026-09-02',
    [
      { id: 'CINCO_ABAS_COMPLETAS', kind: NEW, world: 'system' },
      { id: 'MODOS_DA_IA_EM_PORTUGUES', kind: IMPROVEMENT, world: 'system' },
      {
        id: 'MESCLAR_CONTATO_COM_SEQUENCIA',
        kind: IMPROVEMENT,
        world: 'sales',
      },
    ],
    { prs: [855, 858, 859, 860, 861, 862, 863, 864] }
  ),
  release(
    980,
    '2026-09-02',
    [{ id: 'BRIEFING_LONGO_NAO_CORTA', kind: IMPROVEMENT, world: 'system' }],
    { prs: [856, 857] }
  ),
  release(
    979,
    '2026-09-02',
    [
      {
        id: 'SESSAO_VENCIDA_EM_QUALQUER_COOKIE',
        kind: IMPROVEMENT,
        world: 'system',
      },
    ],
    { prs: [851, 852, 853] }
  ),
  release(
    978,
    '2026-09-02',
    [{ id: 'CONFIGURACOES_EM_ABAS', kind: IMPROVEMENT, world: 'system' }],
    { prs: [849, 850] }
  ),
  release(
    977,
    '2026-09-02',
    [
      { id: 'LEAD_DE_FORMULARIO_NAO_LIDO', kind: IMPROVEMENT, world: 'sales' },
      { id: 'LOGIN_SEM_TELA_PRETA', kind: IMPROVEMENT, world: 'system' },
      { id: 'TEXTO_DE_ATIVIDADE_SEGURO', kind: IMPROVEMENT, world: 'system' },
    ],
    { prs: [843, 844, 847, 848] }
  ),
  release(
    976,
    '2026-09-02',
    [
      { id: 'IA_FALA_O_QUE_FAZ', kind: IMPROVEMENT, world: 'attendance' },
      {
        id: 'RASCUNHO_PRONTO_AO_ABRIR',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      { id: 'ORIGEM_DO_ANUNCIO_VENCE', kind: IMPROVEMENT, world: 'sales' },
      {
        id: 'CAIXA_E_ORIGEM_SEM_CORTE',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [838, 839, 840, 841, 842] }
  ),
  release(
    975,
    '2026-09-01',
    [
      {
        id: 'FIXAR_NO_TOPO',
        kind: NEW,
        world: 'attendance',
      },
      {
        id: 'REORDENAR_POR_ARRASTO',
        kind: NEW,
        world: 'attendance',
      },
      {
        id: 'INSTAGRAM_CONECTA_DE_NOVO',
        kind: IMPROVEMENT,
        world: 'system',
      },
      {
        id: 'HUB_CORE_PRIMEIRO',
        kind: NEW,
        world: 'system',
      },
      {
        id: 'CONTEXTO_DO_LEAD_MAIS_COMPLETO',
        kind: NEW,
        world: 'attendance',
      },
      {
        id: 'CONFIG_DA_CONTA_SALVA_COM_QWEN',
        kind: IMPROVEMENT,
        world: 'system',
      },
      {
        id: 'REGISTRAR_VISITA_NA_AGENDA',
        kind: NEW,
        world: 'attendance',
      },
      {
        id: 'AGENDA_DO_HUB_CONTA_CERTO',
        kind: IMPROVEMENT,
        world: 'system',
      },
      {
        id: 'VISITA_REGISTRADA_APARECE',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'MESSENGER_FIGURINHA_E_POST',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'EMAIL_IMAGEM_INLINE_VIRA_ANEXO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'CAIXA_FACEBOOK_MOSTRA_O_ERRO',
        kind: IMPROVEMENT,
        world: 'system',
      },
    ],
    {
      prs: [
        801, 802, 803, 804, 805, 806, 807, 808, 809, 810, 811, 812, 813, 814,
        815, 817, 818, 819, 820, 816, 821, 822, 823, 824, 825, 826, 827, 828,
        829, 831, 832, 833, 830, 834, 835, 836, 837,
      ],
    }
  ),
  release(
    964,
    '2026-09-01',
    [
      {
        id: 'CABECALHO_MAIS_LIMPO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [791, 792, 793, 794, 795, 796, 797, 798, 799, 800] }
  ),
  release(
    960,
    '2026-08-31',
    [
      {
        id: 'ORIGEM_DO_LEAD',
        kind: NEW,
        world: 'sales',
      },
    ],
    { prs: [787, 788, 789, 790] }
  ),
  release(
    958,
    '2026-08-31',
    [
      {
        id: 'IA_NA_LISTA_DE_CONVERSAS',
        kind: NEW,
        world: 'attendance',
      },
    ],
    { prs: [783, 784, 785, 786] }
  ),
  release(
    956,
    '2026-08-31',
    [
      {
        id: 'RASCUNHO_SEM_BASTIDOR',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [781, 782] }
  ),
  release(
    955,
    '2026-08-31',
    [
      {
        id: 'PRODUTOS_DO_CLIENTE_DE_VERDADE',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'CONTEXTO_MOSTRA_PLANOS_E_COMPRAS',
        kind: NEW,
        world: 'attendance',
      },
      {
        id: 'TELA_FICA_NO_LUGAR_AO_SALVAR',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'ETIQUETA_QUANDO_A_IA_PASSA_PRA_EQUIPE',
        kind: NEW,
        world: 'attendance',
      },
    ],
    { prs: [776, 777, 778, 779, 780] }
  ),
  release(
    953,
    '2026-08-31',
    [
      {
        id: 'SUGESTAO_NAO_SOME_MAIS',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'UM_AVISO_SO_DE_IA_TRABALHANDO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [774, 775] }
  ),
  release(
    952,
    '2026-08-31',
    [
      {
        id: 'VALIDADE_EM_DATA_DE_GENTE',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'CONTEXTO_DO_LEAD_FALA_QUANDO_VAZIO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [772, 773] }
  ),
  release(
    951,
    '2026-08-31',
    [
      {
        id: 'SUGESTAO_CHEGA_SOZINHA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'PEDIDO_DO_CARTAO_VIRA_LINK',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [770, 771] }
  ),
  release(
    950,
    '2026-08-30',
    [
      {
        id: 'CARTOES_DE_APOIO',
        kind: NEW,
        world: 'attendance',
      },
      {
        id: 'CONTEXTO_DO_LEAD',
        kind: NEW,
        world: 'attendance',
      },
      {
        id: 'PERFIL_DE_INTERACAO',
        kind: NEW,
        world: 'attendance',
      },
      {
        id: 'NOTAS_SO_QUANDO_PRECISA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'TEXTO_SEM_ASTERISCOS',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'BUSCA_DE_IMOVEL_AVISA_QUANDO_NAO_RESPONDE',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    {
      prs: [
        748, 749, 750, 751, 752, 755, 756, 757, 758, 759, 760, 761, 762, 763,
        764, 765, 766, 767, 768, 769,
      ],
    }
  ),
  release(
    947,
    '2026-08-30',
    [
      { id: 'CARTOES_DE_APOIO', kind: NEW, world: 'attendance' },
      { id: 'CONTEXTO_DO_LEAD', kind: NEW, world: 'attendance' },
      { id: 'PERFIL_DE_INTERACAO', kind: NEW, world: 'attendance' },
      { id: 'TEXTO_SEM_ASTERISCOS', kind: IMPROVEMENT, world: 'attendance' },
    ],
    { prs: [748, 749, 750, 751, 752, 755, 756, 757, 759, 760, 761] }
  ),
  release(
    945,
    '2026-08-28',
    [
      {
        id: 'SUGESTAO_VOLTOU_NAS_CONTAS_GRANDES',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [746] }
  ),
  release(
    944,
    '2026-08-28',
    [
      {
        id: 'SUGESTAO_VOLTA_APOS_CAMPANHA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [744] }
  ),
  release(
    943,
    '2026-08-28',
    [
      {
        id: 'SUGESTAO_RESPONDE_O_QUE_FOI_PERGUNTADO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'SUGESTAO_NAO_CHEGA_PELA_METADE',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [742] }
  ),
  release(
    942,
    '2026-08-28',
    [
      {
        id: 'SUGESTAO_SEM_RECADO_DE_BASTIDOR',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [740] }
  ),
  release(
    941,
    '2026-08-28',
    [
      {
        id: 'SUGESTAO_PRONTA_QUANDO_VOCE_ABRE',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'ANEXO_ANTIGO_NAO_TRAVA_MAIS',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    // 675, 688, 721, 737 e 738 consertam falha de bastidor (job do Kanban,
    // entrada em grupo, tool do Captain, cobertura do contrato e tetos):
    // entram no rastreio sem item proprio.
    { prs: [675, 688, 721, 727, 737, 738] }
  ),
  release(
    940,
    '2026-08-28',
    [
      {
        id: 'SUGESTAO_RESPEITA_O_RELOGIO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [735] }
  ),
  release(
    939,
    '2026-08-28',
    [
      {
        id: 'SUGESTAO_CHEGA_MAIS_RAPIDO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'IA_SEGUE_O_COMBINADO_DA_CASA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    // 733 liga a escalada de modelo por gatilho, que nasce desligada: nada
    // visivel ate a config ser preenchida, entra sem item.
    { prs: [731, 732, 733] }
  ),
  release(
    938,
    '2026-08-28',
    [
      {
        id: 'CONSULTA_QUE_NAO_RESPONDE_TEM_CAMINHO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [729] }
  ),
  // 726 versiona regras de agente no repo (.cursor/skills): nada visivel na tela.
  release(
    937,
    '2026-08-28',
    [
      {
        id: 'SUGESTAO_SO_FALA_COM_O_CLIENTE',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [725, 726] }
  ),
  release(
    936,
    '2026-08-27',
    [
      {
        id: 'CORPO_DA_CONVERSA_NO_MESMO_CARTAO',
        kind: IMPROVEMENT,
        world: 'attendance',
        routeName: 'home',
      },
    ],
    { prs: [724] }
  ),
  release(
    935,
    '2026-08-27',
    [
      {
        id: 'BORDA_DIREITA_DA_LISTA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [723] }
  ),
  release(
    934,
    '2026-08-27',
    [
      {
        id: 'LISTA_INTEIRA_APOS_NAO_LIDA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'COLUNA_DE_CONVERSAS_ALINHADA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [720] }
  ),
  release(
    933,
    '2026-08-27',
    [
      {
        id: 'AJUSTES_ABREM_CERTO',
        kind: IMPROVEMENT,
        world: 'system',
      },
    ],
    { prs: [718] }
  ),
  release(
    931,
    '2026-08-26',
    [
      {
        // Sem routeName: a lista de ajustes e navegacao, nao tela de conteudo,
        // entao nao tem dica propria para a novidade linkar.
        id: 'AJUSTES_NO_CELULAR',
        kind: IMPROVEMENT,
        world: 'system',
      },
    ],
    // 716 conserta a lupa da busca, que saiu torta nesta mesma entrega: e
    // remendo da 931, nao novidade separada.
    { prs: [714, 716] }
  ),
  release(
    930,
    '2026-08-26',
    [
      {
        id: 'MENU_DAS_CONFIGURACOES',
        kind: IMPROVEMENT,
        world: 'system',
        routeName: 'general_settings_index',
      },
      {
        id: 'INTEGRACAO_SEM_CREDENCIAL',
        kind: IMPROVEMENT,
        world: 'system',
        routeName: 'settings_applications',
        coachKey: 'settings_applications',
      },
      {
        id: 'STATUS_DO_WHATSAPP',
        kind: IMPROVEMENT,
        world: 'system',
      },
    ],
    // 712 renomeia imagem e repositorio para siteup-crm e zapup-api: muda o
    // nome interno das coisas, nao a tela de ninguem, entao entra sem item.
    { prs: [703, 710, 712] }
  ),
  // Primeira imagem em que o gate de cobertura por PR morde: os numeros em
  // `prs` sao cobrados contra o range de commits desde a v928.
  release(
    929,
    '2026-08-26',
    [
      { id: 'BARRA_SO_NO_CELULAR', kind: IMPROVEMENT, world: 'system' },
      {
        id: 'ICONE_DE_INTEGRACAO',
        kind: IMPROVEMENT,
        world: 'system',
        routeName: 'settings_applications',
        coachKey: 'settings_applications',
      },
      {
        id: 'AGENDADA_QUE_NAO_SAI_AVISA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    // 705 e docs(release) e a allowlist do gate ignora, mas entra aqui na
    // mesma: o campo diz "eu olhei", nao "isso virou novidade". O 700 (campo
    // prs) e o 701 (versiona as skills do repo) nao mudam nada visivel e por
    // isso entram sem item.
    { prs: [704, 700, 701, 705, 707] }
  ),
  release(
    928,
    '2026-08-26',
    [
      {
        id: 'CONTATOS_NO_CELULAR',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'contacts_dashboard_index',
        coachKey: 'contacts_dashboard_index',
      },
      {
        id: 'ASSISTENTE_NAO_FICA_MUDO',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      {
        id: 'TROCAR_DE_EMPRESA_NO_HUB',
        kind: IMPROVEMENT,
        world: 'system',
        routeName: 'focus_hub',
        coachKey: 'focus_hub',
      },
    ],
    { prs: [695, 692, 697] }
  ),
  // ---------------------------------------------------------------------
  // v898 a v927 escritas em 26/08/2026, depois do fato. A pratica caiu em
  // 23/08 e a tela ficou parada na v897 enquanto producao ja rodava a v927.
  // O gate do build passou a impedir isso a partir da v929.
  //
  // Reconstruidas a partir do `git log` entre os SHAs de cada build_vNNN.sh
  // na VPS, nao dos comentarios daqueles scripts: v925, v926 e v927 herdaram
  // por copia o mesmo comentario da v924 e descrevem coisa que nao fizeram.
  //
  // Buracos sao deliberados, conforme a regra de que versao sem nada visivel
  // nao ganha entrada: v898 (correcao de seguranca e teste), v899 (a mesma
  // arvore que virou v900), v901 e v904 (builds de staging da Agenda com a
  // flag desligada), v902 (carregamento de biblioteca), v907 e v908 (ajustes
  // da Agenda ainda atras da flag), v914 e v915 (mecanismo interno do
  // assistente), v916 a v918 (builds de teste da fila, publicada na v919),
  // v922 (rebuild da arvore da v921) e v926 (substituida pela v927).
  // ---------------------------------------------------------------------
  release(
    927,
    '2026-08-26',
    [{ id: 'TOPO_DO_APP_NO_CELULAR', kind: IMPROVEMENT, world: 'system' }],
    { prs: [694, 696] }
  ),
  release(
    925,
    '2026-08-26',
    [{ id: 'BARRA_SEGUE_O_TEMA', kind: IMPROVEMENT, world: 'system' }],
    { prs: [693] }
  ),
  release(
    924,
    '2026-08-26',
    [{ id: 'BARRA_DE_BAIXO_NOVA', kind: NEW, world: 'system' }],
    { prs: [691] }
  ),
  release(
    923,
    '2026-08-26',
    [
      {
        id: 'RESOLVER_A_VISTA_NO_CELULAR',
        kind: IMPROVEMENT,
        world: 'attendance',
        routeName: 'inbox_conversation',
        coachKey: 'inbox_conversation',
      },
      { id: 'DICA_NAO_ABRE_SOZINHA', kind: IMPROVEMENT, world: 'system' },
    ],
    { prs: [689, 690] }
  ),
  release(
    921,
    '2026-08-26',
    [
      {
        id: 'FILTRO_E_LISTA_ALINHADOS',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
    ],
    { prs: [688] }
  ),
  release(
    920,
    '2026-08-26',
    [
      {
        id: 'QUEM_FIXA_VOLTA_A_VER_A_FILA',
        kind: IMPROVEMENT,
        world: 'attendance',
        routeName: 'home',
        coachKey: 'home',
      },
      { id: 'ORDENAR_EM_DOIS_MODOS', kind: IMPROVEMENT, world: 'attendance' },
    ],
    { prs: [686, 687] }
  ),
  release(
    919,
    '2026-08-26',
    [
      {
        id: 'FILA_REDESENHADA',
        kind: NEW,
        world: 'attendance',
        routeName: 'home',
        coachKey: 'home',
      },
    ],
    { prs: [683, 685] }
  ),
  release(
    913,
    '2026-08-25',
    [
      {
        id: 'NUMERO_DO_FILTRO',
        kind: IMPROVEMENT,
        world: 'attendance',
        routeName: 'home',
        coachKey: 'home',
      },
    ],
    { prs: [676] }
  ),
  release(
    912,
    '2026-08-24',
    [{ id: 'DESCADASTRO_SO_NO_CLIQUE', kind: IMPROVEMENT, world: 'marketing' }],
    { prs: [674] }
  ),
  release(
    911,
    '2026-08-24',
    [{ id: 'PAGINA_DE_DESCADASTRO', kind: IMPROVEMENT, world: 'marketing' }],
    { prs: [672] }
  ),
  release(
    910,
    '2026-08-24',
    [
      {
        id: 'LISTA_DE_CONVERSAS_MAIS_LARGA',
        kind: IMPROVEMENT,
        world: 'attendance',
      },
      { id: 'ATALHO_DA_AGENDA', kind: IMPROVEMENT, world: 'sales' },
    ],
    { prs: [603, 668, 669, 671] }
  ),
  release(
    909,
    '2026-08-24',
    [
      {
        id: 'AGENDA_PARA_TODAS_AS_CONTAS',
        kind: NEW,
        world: 'sales',
        routeName: 'kanban_board',
        view: 'agenda',
        coachKey: 'kanban_board__agenda',
      },
      { id: 'PUBLICO_DA_CAMPANHA', kind: NEW, world: 'marketing' },
    ],
    { prs: [665, 666, 667] }
  ),
  release(
    906,
    '2026-08-24',
    [
      {
        id: 'BADGE_DE_CONVERSAS',
        kind: IMPROVEMENT,
        world: 'system',
        routeName: 'focus_hub',
        coachKey: 'focus_hub',
      },
      { id: 'CONVITE_DE_TOUR', kind: IMPROVEMENT, world: 'system' },
    ],
    { prs: [661, 662] }
  ),
  release(
    905,
    '2026-08-24',
    [
      {
        id: 'CENTRAL_DE_AGENDAMENTO',
        kind: NEW,
        world: 'sales',
        routeName: 'kanban_board',
        view: 'agenda',
        coachKey: 'kanban_board__agenda',
      },
      { id: 'COMPROMISSO_EM_DUAS_ETAPAS', kind: NEW, world: 'sales' },
    ],
    { prs: [647, 657, 658, 659, 660] }
  ),
  release(
    903,
    '2026-08-24',
    [
      { id: 'RELATORIO_DE_PENDENTES', kind: IMPROVEMENT, world: 'analytics' },
      { id: 'AUDIO_SEM_ESTORVO', kind: IMPROVEMENT, world: 'attendance' },
      { id: 'DATAS_NO_IDIOMA_CERTO', kind: IMPROVEMENT, world: 'system' },
    ],
    { prs: [651, 652, 653, 654, 655] }
  ),
  release(
    900,
    '2026-08-24',
    [
      {
        id: 'APAGAR_CONTATO_SEM_SOBRA',
        kind: IMPROVEMENT,
        world: 'sales',
        routeName: 'contacts_dashboard_index',
        coachKey: 'contacts_dashboard_index',
      },
      { id: 'LIGACAO_NA_JORNADA', kind: IMPROVEMENT, world: 'sales' },
    ],
    { prs: [625, 648, 649] }
  ),
  // As quatro entradas subiram na mesma imagem (tag v897, commit f96fa8ee):
  // nao existe imagem v896, o build pulou de v895 para v897.
  release(897, '2026-08-23', [
    {
      id: 'NOVIDADES',
      kind: NEW,
      world: 'system',
      routeName: 'release_notes',
      coachKey: 'release_notes',
    },
    {
      id: 'VENDAS_NO_HUB',
      kind: NEW,
      world: 'sales',
      routeName: 'focus_hub',
      coachKey: 'focus_hub',
    },
    {
      id: 'TOUR_DA_AGENDA',
      kind: NEW,
      world: 'sales',
      routeName: 'kanban_board',
      view: 'agenda',
      coachKey: 'kanban_board__agenda',
    },
    {
      id: 'BOOT_MAIS_LEVE',
      kind: IMPROVEMENT,
      world: 'system',
    },
  ]),
  release(895, '2026-08-23', [
    {
      id: 'BRASA_BOTAO',
      kind: IMPROVEMENT,
      world: 'system',
    },
    {
      id: 'INTEGRACOES',
      kind: IMPROVEMENT,
      world: 'system',
      routeName: 'settings_applications',
      coachKey: 'settings_applications',
    },
  ]),
  release(894, '2026-08-23', [
    {
      id: 'CENTRAL_AJUDA',
      kind: NEW,
      world: 'system',
      routeName: 'help_guides',
      coachKey: 'help_guides',
    },
  ]),
  release(893, '2026-08-23', [
    {
      id: 'CONVERSA_ABERTA',
      kind: NEW,
      world: 'attendance',
      routeName: 'inbox_conversation',
      coachKey: 'inbox_conversation',
    },
    {
      id: 'PERSONALIZAR_FOCO',
      kind: NEW,
      world: 'system',
    },
    {
      id: 'AJUDA_NO_COMPUTADOR',
      kind: IMPROVEMENT,
      world: 'system',
    },
  ]),
  release(892, '2026-08-23', [
    {
      id: 'AJUDA_DO_KANBAN',
      kind: NEW,
      world: 'sales',
      routeName: 'kanban_board',
      view: 'agenda',
      coachKey: 'kanban_board__agenda',
    },
  ]),
  release(891, '2026-08-23', [
    {
      id: 'CAIXA_DESCONECTADA',
      kind: NEW,
      world: 'attendance',
    },
    {
      id: 'TOUR_PAUSA',
      kind: IMPROVEMENT,
      world: 'system',
    },
  ]),
  release(890, '2026-08-23', [
    {
      id: 'BRASA',
      kind: NEW,
      world: 'system',
    },
  ]),
  release(889, '2026-08-23', [
    {
      id: 'DICA_POR_TELA',
      kind: NEW,
      world: 'system',
    },
    {
      id: 'MANUAL_DA_TELA',
      kind: NEW,
      world: 'system',
      routeName: 'help_guides',
      coachKey: 'help_guides',
    },
    {
      id: 'TOUR_GUIADO',
      kind: NEW,
      world: 'attendance',
      routeName: 'home',
      coachKey: 'home',
    },
  ]),
]);

export const LATEST_BUILD = RELEASES.length ? RELEASES[0].build : 0;

// Espelha `seenVersionOf` do onboardingRegistry: numero finito e maior que
// zero, senao null. Ausente significa que a pessoa nunca abriu a tela.
export const seenBuildOf = value => {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) return numeric;
  return null;
};

export const isUnread = (build, seen) => {
  const seenBuild = seenBuildOf(seen);
  if (seenBuild == null) return true;
  return Number(build) > seenBuild;
};
